import { AlertCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicRecording, RoleEnum } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { getRecordingQueryKey } from "../api/get-recording";
import { useUpdateRecording } from "../api/update-recording";
import {
  UpdateRecordingFormData,
  updateRecordingSchema,
} from "../types/update-recording-schema";

type RecordingSummaryProps = {
  recordingId: string;
  summary: string;
};

export function RecordingSummary(props: RecordingSummaryProps) {
  const { recordingId, summary } = props;
  const [isEditing, setIsEditing] = useState(false);
  const { organisation, hasPermission } = useOrganisation();
  const { mutateAsync: updateRecording, isPending: isUpdating } =
    useUpdateRecording({
      onSuccess: (_, { data }) => {
        setIsEditing(false);
        queryClient.setQueryData(
          getRecordingQueryKey(organisation.id, recordingId),
          (oldData: PublicRecording) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              summary: data.summary,
            };
          },
        );
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  const form = useForm<UpdateRecordingFormData>({
    resolver: zodResolver(updateRecordingSchema),
    defaultValues: {
      summary,
    },
  });

  useEffect(() => {
    form.reset({
      summary,
    });
  }, [summary, form, isEditing]);

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!isEditing) return;

    function handleClickOutside(event: MouseEvent) {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        setIsEditing(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsEditing(false);
      }

      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleUpdateRecording(form.getValues());
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isEditing]);

  const handleUpdateRecording = async (data: UpdateRecordingFormData) => {
    try {
      await updateRecording({
        orgId: organisation.id,
        id: recordingId,
        data,
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  };

  if (isEditing) {
    return (
      <Form {...form}>
        <form
          ref={formRef}
          onSubmit={form.handleSubmit(handleUpdateRecording)}
          className="flex flex-col gap-2"
        >
          <FormField
            control={form.control}
            name="summary"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    className="w-full"
                    {...field}
                    disabled={form.formState.isSubmitting || isUpdating}
                    rows={5}
                  />
                </FormControl>
                <FormDescription className="text-xs">
                  Press Enter to save (Shift + Enter to add a new line)
                </FormDescription>
              </FormItem>
            )}
          />
        </form>
        {form.formState.errors.root && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error updating summary</AlertTitle>
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}
      </Form>
    );
  }

  if (!hasPermission(RoleEnum.CALL_MANAGEMENT)) {
    return <p className="text-sm text-foreground">{summary}</p>;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <p
            className="cursor-pointer text-sm text-foreground"
            onClick={() => setIsEditing(true)}
          >
            {summary}
          </p>
        </TooltipTrigger>
        <TooltipContent side="left">Edit Summary</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
